import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "../i18n/I18nContext";

export default function Footer() {
  const { lang, t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const base = `/${lang}`;

  const platformLinks = [
    { key: "nav_home", to: base, hash: "" },
    { key: "nav_benefits", to: `${base}#benefits`, hash: "benefits" },
    { key: "nav_process", to: `${base}#process`, hash: "process" },
    { key: "nav_transparency", to: `${base}#transparency`, hash: "transparency" },
    { key: "nav_newsletter", to: `${base}#newsletter`, hash: "newsletter" },
  ];

  const legalLinks = [
    { key: "link_privacy", to: `${base}/privacy` },
    { key: "link_cookies", to: `${base}/cookies` },
    { key: "link_terms", to: `${base}/terms` },
  ];

  const isOnHome = location.pathname === base || location.pathname === `${base}/`;

  const handleAnchor = (hash) => (e) => {
    e.preventDefault();
    if (!hash) {
      if (isOnHome) window.scrollTo(0, 0);
      else navigate(base);
      return;
    }
    if (isOnHome) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
    } else {
      navigate(`${base}#${hash}`);
    }
  };

  return (
    <footer className="bg-[#0B2545] text-white/85" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#C85A32] flex items-center justify-center">
                <span className="font-mono text-lg font-bold text-white">XQ</span>
              </div>
              <div>
                <div className="font-bold tracking-widest">XELORIQ</div>
                <div className="text-[11px] text-white/60">{t("meta.site_tagline")}</div>
              </div>
            </div>
            <p className="text-sm text-white/70 leading-relaxed">{t("footer.about")}</p>
          </div>

          {/* Platform */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-[#C85A32] mb-4">
              {t("footer.column_platform")}
            </h3>
            <ul className="space-y-2 text-sm">
              {platformLinks.map((l) => (
                <li key={l.key}>
                  <a
                    href={l.to}
                    onClick={handleAnchor(l.hash)}
                    data-testid={`footer-${l.key.replace("nav_", "")}-link`}
                    className="text-white/80 hover:text-white"
                  >
                    {t(`footer.${l.key}`)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-[#C85A32] mb-4">
              {t("footer.column_legal")}
            </h3>
            <ul className="space-y-2 text-sm">
              {legalLinks.map((l) => (
                <li key={l.key}>
                  <Link
                    to={l.to}
                    data-testid={`footer-${l.key.replace("link_", "")}-link`}
                    className="text-white/80 hover:text-white"
                  >
                    {t(`footer.${l.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-[#C85A32] mb-4">
              {t("footer.column_contact")}
            </h3>
            <div className="space-y-3 text-sm text-white/80">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-white/50">
                  {t("footer.contact_address_label")}
                </div>
                <div>{t("footer.contact_address")}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-white/50">
                  {t("footer.contact_email_label")}
                </div>
                <a
                  href={`mailto:${t("footer.contact_email")}`}
                  data-testid="footer-contact-email"
                  className="underline underline-offset-2"
                >
                  {t("footer.contact_email")}
                </a>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-white/50">
                  {t("footer.contact_hours_label")}
                </div>
                <div>{t("footer.contact_hours")}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Legal band */}
        <div className="mt-12 pt-8 border-t border-white/15 grid grid-cols-1 md:grid-cols-2 gap-8 text-xs text-white/60">
          <div className="space-y-1" data-testid="footer-legal-block">
            <div>{t("footer.legal_company")}</div>
            <div data-testid="footer-legal-cuit">{t("footer.legal_cuit")}</div>
            <div>{t("footer.legal_igj")}</div>
            <div>{t("footer.legal_license")}</div>
            <div>{t("footer.legal_jurisdiction")}</div>
            <div>{t("footer.legal_incorporation")}</div>
            <div>{t("footer.legal_iso")}</div>
            <div>{t("footer.legal_law")}</div>
          </div>
          <div className="space-y-3">
            <p className="leading-relaxed">{t("footer.disclaimer")}</p>
            <p>{t("footer.copyright")}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
